import type {
  FileWithAbsolutePath,
  Folder,
  Origin,
} from "~/features/integration/sharepoint/schema";
import { originSchema } from "~/features/integration/sharepoint/schema";
import type { OpfsFileId } from "~/features/pdf-viewer/types";
import { type ClientEnrichedCircular } from "~/routes/api.circulars";

import { z } from "zod";

export const opfsWorkerMessageSchema = z
  .object({
    type: z.literal("CACHE_SINGLE_BLOB"),
    origin: originSchema,
    cacheId: z.string().min(1),
    blob: z.instanceof(Blob),
  })
  .or(
    z.object({
      type: z.literal("CLEAR_CACHE"),
    }),
  )
  .or(
    z.object({
      type: z.literal("CLEAR_CACHE_ENTRY_BY_ID"),
      payload: z.object({
        origin: originSchema,
        cacheId: z.string().optional(),
      }),
    }),
  )

  .or(
    z.object({
      type: z.literal("CACHE_CIRCULARS"),
      payload: z.custom<ClientEnrichedCircular[]>(),
    }),
  )
  .or(
    z.object({
      type: z.literal("CACHE_TRAIN_OPERATION_DOCUMENTS"),
      payload: z.custom<Folder>(),
    }),
  );

export type OpfsWorkerMessage = z.infer<typeof opfsWorkerMessageSchema>;

export const opfsWorkerResponseSchema = z.object({
  type: z.literal("CACHED"),
  origin: originSchema,
  cacheId: z.string().min(1),
});

export type OpfsWorkerResponse = z.infer<typeof opfsWorkerResponseSchema>;

export const getDirectoryHandle = async (origin: Origin) => {
  // eslint-disable-next-line no-restricted-globals
  const opfsRootHandle = await navigator.storage.getDirectory();
  return await opfsRootHandle.getDirectoryHandle(origin, {
    create: true,
  });
};

async function getFileHandle(id: OpfsFileId) {
  const directoryHandle = await getDirectoryHandle(id.origin);
  return await directoryHandle.getFileHandle(id.cacheId, {
    create: false,
  });
}

export const readBlob = async (
  opsField: OpfsFileId,
  onError?: (error: unknown) => void,
) => {
  try {
    const fileHandle = await getFileHandle(opsField);
    const file = await fileHandle.getFile();
    return new Blob([file]);
  } catch (error) {
    onError?.(error);
    return null;
  }
};

export const blobExists = async (opsField: OpfsFileId) => {
  try {
    await getFileHandle(opsField);
    return true;
  } catch (_) {
    return false;
  }
};

export const flattenFileTree = (
  tree: Folder,
  basePath: string[] = [],
): FileWithAbsolutePath[] => {
  const flattenedFiles: FileWithAbsolutePath[] = [];

  for (const item of tree.contents) {
    if (item.type === "folder") {
      flattenedFiles.push(...flattenFileTree(item, [...basePath, item.name]));
    } else if (item.type === "file") {
      const flattenedFile: FileWithAbsolutePath = {
        ...item,
        absolutePath: [...basePath, item.name],
      };
      flattenedFiles.push(flattenedFile);
    }
  }

  return flattenedFiles;
};
